import { SwapPairPrice } from '../hooks/useSwap'
import useBalances from '../hooks/useBalances'
import { ChainOption, PlugToken } from '../interfaces/common'
import { Chain } from '../interfaces/common'
import React, { PropsWithChildren, createContext, useContext, useState } from 'react'
import useConfig from '../hooks/useConfig'
import { ICP_ICP_ID, BOB_ICP_ID, ODINBTC_ID, ODINDOG_ID } from '../constants/tokenIds'

export const DEFAULT_SWAP_TOKENS = {
  [Chain.ICP.toString()]: { from: ICP_ICP_ID, to: BOB_ICP_ID },
  [Chain.ODIN.toString()]: { from: ODINBTC_ID, to: ODINDOG_ID },
}

interface ISwapProvider {
  fromToken?: PlugToken
  setFromToken: (token?: PlugToken) => void
  toToken?: PlugToken
  setToToken: (token?: PlugToken) => void
  quotes: SwapPairPrice[]
  setQuotes: (quotes: SwapPairPrice[]) => void
  selectedProvider?: string
  setSelectedProvider: (provider?: string) => void
  manualProvider?: string
  setManualProvider: (provider: string) => void
  amount: string
  setAmount: (newAmmount: string) => void
  toAmount: string
  setToAmount: (newAmount: string) => void
  swapReviewVisible: boolean
  setSwapReviewVisible: (visible: boolean) => void
  swapConfigVisible: boolean
  setSwapConfigVisible: (visible: boolean) => void
  chainMode: ChainOption
  setChainMode: (chain: ChainOption) => void
  preselectChain: (chain: ChainOption) => void
}

const SwapContext = createContext<ISwapProvider>(null as any)

export const SwapProvider = ({ children }: PropsWithChildren) => {
  const [amount, setAmount] = useState<string>('')
  const [fromToken, setFromToken] = useState<PlugToken>()
  const [toToken, setToToken] = useState<PlugToken>()
  const [quotes, setQuotes] = useState<SwapPairPrice[]>([])
  const [selectedProvider, setSelectedProvider] = useState<string>()
  const [manualProvider, setManualProvider] = useState<string>()
  const [toAmount, setToAmount] = useState<string>('')
  const [swapReviewVisible, setSwapReviewVisible] = useState(false)
  const [swapConfigVisible, setSwapConfigVisible] = useState(false)
  const [chainMode, setChainMode] = useState<ChainOption>(Chain.ICP)
  const { tokens } = useConfig()

  const { refreshBalance } = useBalances()

  const onSetFromToken = (token: PlugToken | undefined) => {
    setQuotes([])
    setFromToken(token)

    if (token) {
      setChainMode(token.chain)
      refreshBalance(token)
    }
  }

  const onSetToToken = (token: PlugToken | undefined) => {
    setQuotes([])
    setToToken(token)
  }

  const onSetChainMode = (chain: ChainOption) => {
    setChainMode(chain)

    const { from, to } = DEFAULT_SWAP_TOKENS[chain]

    if (fromToken?.chain !== chain) {
      setFromToken(tokens.find((t) => t.id === from))
    }

    if (toToken?.chain !== chain) {
      setToToken(tokens.find((t) => t.id === to))
    }
  }

  return (
    <SwapContext.Provider
      value={{
        amount,
        setAmount,
        fromToken,
        setFromToken: onSetFromToken,
        toToken,
        setToToken: onSetToToken,
        quotes,
        selectedProvider,
        setQuotes,
        setSelectedProvider,
        toAmount,
        setToAmount,
        manualProvider,
        setManualProvider,
        swapReviewVisible,
        setSwapReviewVisible,
        swapConfigVisible,
        setSwapConfigVisible,
        chainMode,
        setChainMode: onSetChainMode,
        preselectChain: setChainMode,
      }}
    >
      {children}
    </SwapContext.Provider>
  )
}

export const useSwapContext = () => useContext(SwapContext)
